import React, { useEffect, useRef, useState } from 'react';

import { Box, Tooltip, Typography, alpha, useTheme } from '@mui/material';

interface CellTooltipProps {
  value: string | number | null | undefined;
  maxWidth?: number;
  disabled?: boolean;
}

interface LabeledCellProps {
  value: string | null | undefined;
  getLabel: (value: string) => string;
  getTooltip: (value: string) => string;
  maxWidth?: number;
  disabled?: boolean;
}

interface HeaderTooltipProps {
  title: string;
  tooltipText: string;
  disabled?: boolean;
}

export const CellWithTooltip: React.FC<CellTooltipProps> = ({ value, maxWidth = 150, disabled = false }) => {
  const theme = useTheme();
  const displayValue = value?.toString() || '—';
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const isOverflow = textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsOverflowing(isOverflow);
      }
    };

    checkOverflow();
    // Re-check on window resize
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [displayValue]);

  const content = (
    <Box
      ref={textRef}
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: maxWidth,
        width: '100%',
      }}
    >
      {displayValue}
    </Box>
  );

  // If tooltips are disabled or content is not overflowing, just return the content
  if (disabled || !isOverflowing) {
    return content;
  }

  return (
    <Tooltip
      title={displayValue}
      placement="top"
      arrow
      enterDelay={300}
      leaveDelay={200}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            color: theme.palette.text.primary,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            borderRadius: 2,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
            fontSize: theme.typography.body2.fontSize,
            fontWeight: theme.typography.fontWeightRegular,
            p: theme.spacing(1, 1.5),
            maxWidth: theme.spacing(50),
            zIndex: theme.zIndex.tooltip,
            '& .MuiTooltip-arrow': {
              color: alpha(theme.palette.background.paper, 0.95),
              '&::before': {
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.95),
              },
            },
          },
        },
      }}
    >
      {content}
    </Tooltip>
  );
};

export const LabeledCellWithTooltip: React.FC<LabeledCellProps> = ({
  value,
  getLabel,
  getTooltip,
  maxWidth = 200,
  disabled = false,
}) => {
  const theme = useTheme();
  if (!value) return <Box>—</Box>;

  const displayLabel = value;
  const tooltipText = getTooltip(value);

  const content = (
    <Box
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: maxWidth,
        width: '100%',
        ...(!disabled && {
          cursor: 'help',
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: '3px',
          textDecorationColor: alpha(theme.palette.text.primary, 0.3),
        }),
      }}
    >
      {displayLabel}
    </Box>
  );

  if (disabled) {
    return content;
  }

  return (
    <Tooltip
      title={
        <Box>
          <Box sx={{ fontWeight: theme.typography.fontWeightMedium, mb: 0.5 }}>{displayLabel}</Box>
          <Box sx={{ fontSize: theme.typography.caption.fontSize, opacity: 0.9 }}>{tooltipText}</Box>
        </Box>
      }
      placement="top"
      arrow
      enterDelay={200}
      leaveDelay={100}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: alpha(theme.palette.background.paper, 0.98),
            color: theme.palette.text.primary,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            borderRadius: 2,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
            fontSize: theme.typography.body2.fontSize,
            p: theme.spacing(1.25, 1.75),
            maxWidth: theme.spacing(43.75),
            zIndex: theme.zIndex.tooltip,
            '& .MuiTooltip-arrow': {
              color: alpha(theme.palette.background.paper, 0.98),
              '&::before': {
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.98),
              },
            },
          },
        },
      }}
    >
      {content}
    </Tooltip>
  );
};

export const HeaderWithTooltip: React.FC<HeaderTooltipProps> = ({ title, tooltipText, disabled = false }) => {
  const theme = useTheme();

  const content = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...(!disabled && { cursor: 'help' }) }}>
      <Typography sx={{ fontWeight: theme.typography.fontWeightMedium, fontSize: theme.typography.body2.fontSize }}>
        {title}
      </Typography>
      {!disabled && (
        <Typography sx={{ fontSize: theme.typography.caption.fontSize, color: 'text.secondary' }}>ⓘ</Typography>
      )}
    </Box>
  );

  if (disabled) {
    return content;
  }

  return (
    <Tooltip
      title={tooltipText}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: alpha(theme.palette.background.paper, 0.98),
            color: theme.palette.text.primary,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            borderRadius: 2,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
            fontSize: theme.typography.body2.fontSize,
            fontWeight: theme.typography.fontWeightRegular,
            p: theme.spacing(1.25, 1.75),
            maxWidth: theme.spacing(43.75),
            zIndex: theme.zIndex.tooltip,
            '& .MuiTooltip-arrow': {
              color: alpha(theme.palette.background.paper, 0.98),
              '&::before': {
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.98),
              },
            },
          },
        },
      }}
    >
      {content}
    </Tooltip>
  );
};
